import React, { useContext, useState, useEffect, forwardRef, useImperativeHandle } from "react";
import { Slider } from "primereact/slider";
import { InputNumber } from "primereact/inputnumber";
import { Calendar } from "primereact/calendar";
import { AddPropertyContext } from "./AddPropertyContext";
import { formatCurrency, getDateStringWithGivenFormat, redirectToRPData } from "../../../utils/commonUtil";

interface CurrentStatusProps {
  isEditMode?: boolean;
}

const CurrentStatus = forwardRef((props: CurrentStatusProps, ref) => {
  const context = useContext(AddPropertyContext);
  const [purchaseDate, setPurchaseDate] = useState<Date | null>(null);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  useImperativeHandle(ref, () => ({
    validate: () => {
      const newErrors: { [key: string]: string } = {};

      if (!purchaseDate) {
        newErrors["PurchaseDate"] = "Initial purchase date is required.";
      }

      if (props.isEditMode) {
        if (!property.PurchasePrice || property.PurchasePrice === 0) {
          newErrors["PurchasePrice"] = "Initial purchase price must be greater than 0.";
        }
        if (!property.EstimatedMarketValue || property.EstimatedMarketValue === 0) {
          newErrors["EstimatedMarketValue"] = "Market value must be greater than 0.";
        }
      }

      if (!props.isEditMode) {
        if (!property.AmountOwning) {
          newErrors["AmountOwing"] = "Amount owing must be greater than 0.";
        }

        if (!property.Current_InitialPurchasePrice || property.Current_InitialPurchasePrice === 0) {
          newErrors["Current_InitialPurchasePrice"] = "Initial purchase price must be greater than 0.";
        }
        if (!property.Current_MarketValue || property.Current_MarketValue === 0) {
          newErrors["Current_MarketValue"] = "Market value must be greater than 0.";
        }
      }

      setErrors(newErrors);
      return Object.keys(newErrors).length === 0;
    },
  }));

  useEffect(() => {
    if (context?.property?.PurchaseDate) {
      try {
        const parsedDate = new Date(context.property.PurchaseDate);

        const parts = context.property.PurchaseDate.split("/");
        if (parts.length === 3 && parts[0] && parts[1] && parts[2]) {
          const day = parseInt(parts[0], 10);
          const month = parseInt(parts[1], 10);
          const year = parseInt(parts[2], 10);
          parsedDate.setFullYear(year, month - 1, day);
        }
        if (!isNaN(parsedDate.getTime())) setPurchaseDate(parsedDate);
      } catch (e) {
        setPurchaseDate(null);
      }
    }
  }, [context?.property?.PurchaseDate]);

  if (!context) {
    return <div>Loading...</div>;
  }

  const { property, setProperty } = context;

  const handleInputChange = (field: keyof typeof property, value: any) => {
    setProperty({ ...property, [field]: value });
  };

  const handleDateChange = (date: Date | null) => {
    if (date instanceof Date && !isNaN(date.getTime())) {
      setPurchaseDate(date);
      const purchaseDateStr = getDateStringWithGivenFormat(date, "dd/mm/yy");
      setProperty({
        ...property,
        PurchaseDate: purchaseDateStr,
        ...(!props.isEditMode &&
          !property.isProRatedExpenseDateManuallySet && {
            Year1ProRataDateExpense: purchaseDateStr,
          }),
      });
    }
  };

  const renderAddMode = () => {
    return (
      <>
        <div className="flex flex-row gap-4 mb-4">
          <div className="flex-0 flex-col w-1/2 gap-2">
            <div className="flex items-center">
              <label className="w-1/4">Amount owing ($)</label>
              <Slider
                id="apcur-sldr-amountowing"
                data-testid="apcur-sldr-amountowing"
                value={(property.AmountOwning || 0) > 1500000 ? 1500000 : property.AmountOwning || 0}
                onChange={(e: any) => handleInputChange("AmountOwning", e.value)}
                className="w-2/4 px-4"
                max={1500000}
              />

              <InputNumber
                id="apcur-num-amountowing"
                data-testid="apcur-num-amountowing"
                value={property.AmountOwning}
                onValueChange={(e) => handleInputChange("AmountOwning", e.value)}
                className={`w-1/4 ${errors.AmountOwing ? "p-invalid" : ""}`}
              />
            </div>
            {errors.AmountOwing && <div className="text-red-500 text-sm mt-1 text-right">{errors.AmountOwing}</div>}
          </div>
          <div className="flex-0 flex-col w-1/2 gap-2">
            <div className="flex items-center">
              <label className="w-1/4">Initial purchase price ($)</label>
              <Slider
                id="apcur-sldr-initialprice"
                data-testid="apcur-sldr-initialprice"
                value={
                  property?.Current_InitialPurchasePrice && property.Current_InitialPurchasePrice > 1500000
                    ? 1500000
                    : property.Current_InitialPurchasePrice || 0
                }
                onChange={(e: any) => handleInputChange("Current_InitialPurchasePrice", e.value)}
                className="w-2/4 px-4 mr-3"
                max={1500000}
              />
              <InputNumber
                id="apcur-num-initialprice"
                data-testid="apcur-num-initialprice"
                value={property.Current_InitialPurchasePrice || 0}
                onValueChange={(e) => handleInputChange("Current_InitialPurchasePrice", e.value!)}
                className={`w-1/4 ${errors.Current_InitialPurchasePrice ? "p-invalid" : ""}`}
              />
            </div>
            {errors.Current_InitialPurchasePrice && (
              <div className="text-red-500 text-sm mt-1 text-right">{errors.Current_InitialPurchasePrice}</div>
            )}
          </div>
        </div>
        <div className="flex flex-row gap-4 mb-4">
          <div className="flex-0 flex-col w-1/2 gap-2">
            <div className="flex items-center">
              <label className="w-1/4">Market value now ($)</label>
              <Slider
                id="apcur-sldr-marketvalue"
                data-testid="apcur-sldr-marketvalue"
                value={
                  property?.Current_MarketValue && property.Current_MarketValue > 1500000
                    ? 1500000
                    : property.Current_MarketValue || 0
                }
                onChange={(e: any) => handleInputChange("Current_MarketValue", e.value)}
                className="w-2/4 px-4 mr-3"
                max={1500000}
              />
              <InputNumber
                id="apcur-num-marketvalue"
                data-testid="apcur-num-marketvalue"
                value={property.Current_MarketValue || 0}
                onValueChange={(e) => handleInputChange("Current_MarketValue", e.value!)}
                className={`w-1/4 ${errors.Current_MarketValue ? "p-invalid" : ""}`}
              />
            </div>
            {errors.Current_MarketValue && (
              <div className="text-red-500 text-sm mt-1 text-right">{errors.Current_MarketValue}</div>
            )}
          </div>

          <div className="flex-0 flex-col w-1/2 gap-2">
            <div className="flex items-center">
              <label className="w-1/4">Initial purchase date</label>
              <Calendar
                value={purchaseDate}
                onChange={(e: any) => handleDateChange(e.value)}
                dateFormat="dd/mm/yy"
                className={`w-3/4 ${errors.PurchaseDate ? "p-invalid" : ""}`}
                showIcon
              />
            </div>
            {errors.PurchaseDate && <div className="text-red-500 text-sm mt-1 text-right">{errors.PurchaseDate}</div>}
          </div>
        </div>
      </>
    );
  };

  const renderEditMode = () => {
    return (
      <>
        <div className="flex flex-row gap-4 mb-4">
          <div className="flex-0 flex-col w-1/2 gap-2">
            <div className="flex items-center">
              <label className="w-1/4">Amount owing ($)</label>

              <label className="w-1/4">{formatCurrency(property.ExistingLoans?.[0]?.Balance || 0)}</label>
            </div>
          </div>
          <div className="flex-0 flex-col w-1/2 gap-2">
            <div className="flex items-center">
              <label className="w-1/4">Initial purchase price ($)</label>
              <Slider
                id="apcur-sldr-purchaseprice"
                data-testid="apcur-sldr-purchaseprice"
                value={
                  property?.PurchasePrice && property.PurchasePrice > 1500000 ? 1500000 : property.PurchasePrice || 0
                }
                onChange={(e: any) => handleInputChange("PurchasePrice", e.value)}
                className="w-2/4 px-4 mr-3"
                max={1500000}
              />
              <InputNumber
                id="apcur-num-purchaseprice"
                data-testid="apcur-num-purchaseprice"
                value={property.PurchasePrice || 0}
                onValueChange={(e) => handleInputChange("PurchasePrice", e.value!)}
                className={`w-1/4 ${errors.PurchasePrice ? "p-invalid" : ""}`}
              />
            </div>
            {errors.PurchasePrice && <div className="text-red-500 text-sm mt-1 text-right">{errors.PurchasePrice}</div>}
          </div>
        </div>
        <div className="flex flex-row gap-4 mb-4">
          <div className="flex-0 flex-col w-1/2 gap-2">
            <div className="flex items-center">
              <label className="w-1/4">Market value now ($)</label>
              <Slider
                id="apcur-sldr-estmarketvalue"
                data-testid="apcur-sldr-estmarketvalue"
                value={
                  property?.EstimatedMarketValue && property.EstimatedMarketValue > 1500000
                    ? 1500000
                    : property.EstimatedMarketValue || 0
                }
                onChange={(e: any) => handleInputChange("EstimatedMarketValue", e.value)}
                className="w-2/4 px-4 mr-3"
                max={1500000}
              />
              <InputNumber
                id="apcur-num-estmarketvalue"
                data-testid="apcur-num-estmarketvalue"
                value={property.EstimatedMarketValue || 0}
                onValueChange={(e) => handleInputChange("EstimatedMarketValue", e.value!)}
                className={`w-1/4 ${errors.EstimatedMarketValue ? "p-invalid" : ""}`}
              />
            </div>
            {errors.EstimatedMarketValue && (
              <div className="text-red-500 text-sm mt-1 text-right">{errors.EstimatedMarketValue}</div>
            )}
          </div>

          <div className="flex-0 flex-col w-1/2 gap-2">
            <div className="flex items-center">
              <label className="w-1/4">Initial purchase date</label>
              <Calendar
                value={purchaseDate}
                onChange={(e: any) => handleDateChange(e.value)}
                dateFormat="dd/mm/yy"
                className={`w-3/4 ${errors.PurchaseDate ? "p-invalid" : ""}`}
                showIcon
              />
            </div>
            {errors.PurchaseDate && <div className="text-red-500 text-sm mt-1 text-right">{errors.PurchaseDate}</div>}
          </div>
        </div>
      </>
    );
  };

  return (
    <div className="relative rounded-lg p-4 border-solid border border-gray-300 bg-white">
      <div className="default-profile-header-container">
        <h3 className="text-[var(--primary-color)] mt-0 m-1 text-lg">Current status</h3>
      </div>
      {props.isEditMode ? renderEditMode() : renderAddMode()}
      <p className="text-sm mt-4">
        * We are unable to provide an estimate of value at this stage.
        <br />
        You can use My valuer to{" "}
        <a
          href="#"
          id="apcur-lnk-getestimate"
          data-testid="apcur-lnk-getestimate"
          className="text-primary no-underline"
          onClick={redirectToRPData}
        >
          get an estimate
        </a>{" "}
        (if available) or use the slider or entry field to add your own estimate.
      </p>
    </div>
  );
});

export default CurrentStatus;
